/**
 * useChatExpandedAddress — `?chat=expanded` opens the chat full screen with its draft box ready
 * (tri_mvfr3ud2_6eea3bbc90, 2026-10-20). The words of the address are src/lib/threadDeepLink.ts's.
 *
 * WHY IT WATCHES THE ROUTER, NOT THE PAGE LOAD. The engine's own reader of the app's address intents runs once per
 * page load, once its thread store is ready. A page that needs a sign-in sends a visitor to /auth with the whole
 * address kept (ProtectedRoute's returnUrl), and the sign-in then moves to it INSIDE the page: by then the once-a-load
 * reader has long run, on /auth. So this reads every address the app arrives at, wherever the chat shows:
 *   · this tab's memory is set to open and full screen (chatModeMemory.ts), never the person's default, so an engine
 *     that mounts after the address is cleaned up still starts expanded (bootChatMode.ts reads the address itself
 *     while it is there);
 *   · the open is asked for as a presentation, full screen, with the caret in the draft box (useGlobalChat.ts), held
 *     until the engine listens if it has not loaded yet (chatIntentBuffer.ts);
 *   · the address loses `chat=expanded` (only that: a `thread=` beside it is still the engine's to read), so a reload
 *     or a copied link does not ask again.
 * Where the chat is hidden (the sign-in and onboarding steps) nothing happens until the app arrives somewhere it shows.
 * While the onboarding check is still loading the chat shows (App.tsx), so an address read then is honored as anywhere
 * else: this tab's memory keeps the chat open and full screen through any onboarding that follows.
 *
 * Leaf: no engine import (scripts/check-chat-first-paint.mjs).
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseChatExpandedIntent, searchWithoutChatExpanded } from '../lib/threadDeepLink';
import { writeTabMode } from '../components/GlobalChatSystem/chatModeMemory';
import { GLOBAL_CHAT_OPEN_EVENT } from '../components/GlobalChatSystem/chatIntentBuffer';

/** What the address asks of the engine (useGlobalChat.ts reads each field). */
export const CHAT_EXPANDED_OPEN_DETAIL = Object.freeze({ presentation: true, fullScreen: true, focusComposer: true });

export function useChatExpandedAddress(chatShown: boolean): void {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!chatShown || !parseChatExpandedIntent(location.search)) return;
    writeTabMode({ open: true, fullScreen: true });
    document.dispatchEvent(new CustomEvent(GLOBAL_CHAT_OPEN_EVENT, { detail: { ...CHAT_EXPANDED_OPEN_DETAIL } }));
    // The page's own address while it still asks for this: the engine cleans up the intents it read (`thread=`, `ws=`)
    // with history.replaceState, which the router never sees, so its copy could put them back.
    const live = typeof window !== 'undefined' && parseChatExpandedIntent(window.location.search) ? window.location.search : location.search;
    navigate(
      { pathname: location.pathname, search: searchWithoutChatExpanded(live), hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [chatShown, location.pathname, location.search, location.hash, location.state, navigate]);
}
